import { baseBits, blockedBits } from '../entropy/estimates';
import type { Signal } from './types';

export interface SignalOptions {
  display?: string;
  /** Override the estimate from estimates.ts (value-aware adjustments). */
  bits?: number | null;
  note?: string;
  randomized?: boolean;
}

/** Build an available signal, taking the bit estimate from estimates.ts unless overridden. */
export function signal(id: string, label: string, value: unknown, opts: SignalOptions = {}): Signal {
  const s: Signal = {
    id,
    label,
    value: value === undefined ? null : value,
    display: opts.display ?? formatValue(value),
    bits: opts.bits !== undefined ? opts.bits : baseBits(id),
    available: true,
  };
  if (opts.note) s.note = opts.note;
  if (opts.randomized) s.randomized = true;
  return s;
}

/**
 * A signal for an API this browser does not implement. Scores 0 bits: its absence is already
 * implied by the browser and version.
 */
export function unavailable(id: string, label: string, note: string): Signal {
  return { id, label, value: null, display: 'Not available', bits: baseBits(id) === null ? null : 0, available: false, note };
}

/**
 * A signal for an API that exists but is disabled or blocked (by a setting, extension or privacy
 * mode). Being blocked is uncommon, so it costs a small number of bits.
 */
export function blocked(id: string, label: string, note: string): Signal {
  return { id, label, value: null, display: 'Blocked', bits: blockedBits(id), available: false, note };
}

/**
 * Run one signal probe fail-safe: any exception becomes an `available: false` signal.
 * One broken API must never break the rest of the report.
 */
export async function safe(id: string, label: string, probe: () => Signal | Promise<Signal>): Promise<Signal> {
  try {
    return await probe();
  } catch (err) {
    return blocked(id, label, describeError(err));
  }
}

export function describeError(err: unknown): string {
  if (err instanceof Error) {
    if (err.name === 'SecurityError' || err.name === 'NotAllowedError') return `Blocked by browser (${err.name})`;
    return `Error: ${err.message || err.name}`;
  }
  return `Error: ${String(err)}`;
}

export function formatValue(value: unknown): string {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'string') return value === '' ? '(empty string)' : value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    if (value.length === 0) return '(none)';
    if (value.every((v) => typeof v !== 'object' || v === null)) return value.map((v) => String(v)).join(', ');
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

/** Reject after `ms` milliseconds. */
export function withTimeout<T>(promise: Promise<T>, ms: number, what = 'operation'): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new TimeoutError(`${what} timed out after ${ms} ms`)), ms);
    promise.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (err: unknown) => {
        clearTimeout(timer);
        reject(err instanceof Error ? err : new Error(String(err)));
      },
    );
  });
}

export class TimeoutError extends Error {
  override name = 'TimeoutError';
}

export const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/** Typed access to non-standard or not-yet-typed properties without `any`. */
export function prop<T = unknown>(obj: unknown, key: string): T | undefined {
  if (obj === null || obj === undefined) return undefined;
  try {
    return (obj as Record<string, unknown>)[key] as T | undefined;
  } catch {
    return undefined;
  }
}

export const hasDocument = () => typeof document !== 'undefined' && !!document.body;

/** Stable, sorted copy of a string list. */
export const sorted = (list: Iterable<string>) => Array.from(new Set(list)).sort();

/** Short hex prefix for display. */
export const short = (hex: string, n = 16) => hex.slice(0, n);
